import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCard,
  MiDetailFields,
  MiFileUpload,
  MiLoader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  FILE_MIME_TYPE_EXTENSIONS,
  PROPERTY_TYPE,
} from "@miplatform/constants";
import {
  mdiAccountOutline,
  mdiFileDocumentOutline,
  mdiHomeRoof,
} from "@mdi/js";
import { Box, Collapse } from "@mui/material";
import { ContactsSearch } from "../ContactsSearch/ContactsSearch";
import { Footer } from "../Footer/Footer";
import { ADD_HOME_SECTIONS } from "./addHomeConstants";

export const AddHomeForm = ({
  isLoading,
  isSubmitDisabled,
  isCreateCustomerDisabled,
  hidePropertyType = false,
  hideFooter = false,
  showCreateContact = false,
  createCustomer,
  edit,
  homeContacts,
  detailFields,
  allowedFileFormats,
  handleFileChange,
  setHomeContacts,
  setIsContactEditing = () => {},
  onCancelContactCreation = () => {},
  onResetForm = () => {},
  onCancel = () => {},
  onCreate = () => {},
}) => {
  const { classes, theme } = useStyles();

  const handleClose = () => {
    onResetForm();
    onCancel();
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.main}>
        <MiLoader type="linear" isLoading={isLoading} />
        {!hidePropertyType && (
          <MiCard
            title={ADD_HOME_SECTIONS.PROPERTY_TYPE}
            leftIcon={mdiAccountOutline}
            containerStyles={{ padding: theme.spacing(2) }}
          >
            <MiDetailFields
              detailFields={detailFields[ADD_HOME_SECTIONS.PROPERTY_TYPE]}
              edit={edit}
            />
          </MiCard>
        )}
        {showCreateContact ? (
          <MiCard
            title={ADD_HOME_SECTIONS.CREATE_CUSTOMER}
            leftIcon={mdiAccountOutline}
            containerStyles={{ padding: theme.spacing(2) }}
          >
            <MiDetailFields
              detailFields={detailFields[ADD_HOME_SECTIONS.CREATE_CUSTOMER]}
              edit={edit}
            />
            <Box className={classes.btnContainer}>
              <MiButton
                title="Cancel"
                variant={BUTTON_VARIANTS.OUTLINED}
                color={BUTTON_COLORS.BLACK}
                onClick={onCancelContactCreation}
              />
              <MiButton
                title="Save"
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.PRIMARY}
                disabled={isCreateCustomerDisabled || isLoading}
                onClick={createCustomer}
              />
            </Box>
          </MiCard>
        ) : (
          <MiCard
            title={ADD_HOME_SECTIONS.CUSTOMER_SEARCH}
            leftIcon={mdiAccountOutline}
            containerStyles={{ padding: theme.spacing(2) }}
          >
            <MiDetailFields
              detailFields={detailFields[ADD_HOME_SECTIONS.CUSTOMER_SEARCH]}
              edit={edit}
            />
          </MiCard>
        )}
        <MiCard
          title={ADD_HOME_SECTIONS.ADDRESS_INFORMATION}
          leftIcon={mdiHomeRoof}
          containerStyles={{ padding: theme.spacing(2) }}
        >
          <MiDetailFields
            detailFields={detailFields[ADD_HOME_SECTIONS.ADDRESS_INFORMATION]}
            edit={edit}
          />
        </MiCard>
        <MiCard
          title={ADD_HOME_SECTIONS.PROPERTY_CONTACTS}
          leftIcon={mdiAccountOutline}
          containerStyles={{ padding: theme.spacing(2) }}
        >
          <ContactsSearch
            contacts={homeContacts}
            setContacts={setHomeContacts}
            defaultAccountId={edit.getValue("customer")?.id}
            setIsContactEditing={setIsContactEditing}
          />
        </MiCard>
        <MiCard
          title={ADD_HOME_SECTIONS.ADDITIONAL_INFORMATION}
          leftIcon={mdiHomeRoof}
          containerStyles={{ padding: theme.spacing(2) }}
        >
          <MiDetailFields
            detailFields={
              detailFields[ADD_HOME_SECTIONS.ADDITIONAL_INFORMATION]
            }
            edit={edit}
          />
        </MiCard>
        <Collapse
          in={
            edit.getValue("propertyType") ===
            PROPERTY_TYPE.NEW_HOME_CONSTRUCTION
          }
        >
          <MiCard
            title={ADD_HOME_SECTIONS.PROPERTY_INFORMATION}
            leftIcon={mdiHomeRoof}
            rightComponent={
              <MiDetailFields
                detailFields={
                  detailFields[
                    ADD_HOME_SECTIONS.PROPERTY_INFORMATION_CHECKBOXES
                  ]
                }
                edit={edit}
              />
            }
            containerStyles={{ padding: theme.spacing(2) }}
          >
            <MiDetailFields
              detailFields={
                detailFields[ADD_HOME_SECTIONS.PROPERTY_INFORMATION]
              }
              edit={edit}
            />
          </MiCard>
        </Collapse>
        <MiCard
          title={ADD_HOME_SECTIONS.DOCUMENTS}
          leftIcon={mdiFileDocumentOutline}
          containerStyles={{ padding: theme.spacing(2) }}
        >
          <MiFileUpload
            dragAndDrop
            onChange={handleFileChange}
            validFileTypes={allowedFileFormats}
            acceptableFormatText={`Acceptable formats: ${allowedFileFormats
              .map((mimeType) => FILE_MIME_TYPE_EXTENSIONS[mimeType])
              .join(", ")}`}
          />
        </MiCard>
      </Box>
      <Footer
        sticky
        show={!hideFooter}
        height={50}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: handleClose,
          },
          {
            name: "Save and Create Order",
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: onCreate,
            disabled: isSubmitDisabled,
          },
        ]}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    paddingBottom: theme.spacing(2),
  },
  main: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  btnContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(4),
    margin: theme.spacing(2, 3),
  },
}));
